import React, { FC, ReactNode, SyntheticEvent } from "react";

import { alpha, styled } from "@mui/material/styles";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";

const StyledTreeView = styled(SimpleTreeView, {
	shouldForwardProp: (prop) => prop !== "dense",
})<{ dense?: boolean }>(({ theme, dense }) => {
	const rowMinH = dense ? 40 : 48;

	return {
		marginBottom: dense ? theme.spacing(2) : theme.spacing(3),

		// Indentation guide for children
		"& .MuiTreeItem-group": {
			marginLeft: theme.spacing(2),
			paddingLeft: theme.spacing(1.25),
			borderLeft: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
		},

		// Each row content
		"& .MuiTreeItem-content": {
			minHeight: rowMinH,
			padding: theme.spacing(0, 1),
			borderRadius: 8,
			transition: "background-color .15s ease, box-shadow .15s ease",
			"&:hover": {
				backgroundColor:
					theme.palette.mode === "light"
						? alpha(theme.palette.text.primary, 0.04)
						: alpha(theme.palette.text.primary, 0.08),
			},

			// Focus ring
			"&.Mui-focused": {
				boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
				backgroundColor:
					theme.palette.mode === "light"
						? alpha(theme.palette.primary.main, 0.04)
						: alpha(theme.palette.primary.main, 0.1),
			},

			// Selected row
			"&.Mui-selected": {
				backgroundColor:
					theme.palette.mode === "light"
						? alpha(theme.palette.primary.main, 0.08)
						: alpha(theme.palette.primary.main, 0.18),
				boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}`,
				"&:hover": {
					backgroundColor:
						theme.palette.mode === "light"
							? alpha(theme.palette.primary.main, 0.12)
							: alpha(theme.palette.primary.main, 0.24),
				},
			},

			// Icon spacing/align
			"& .MuiTreeItem-iconContainer": {
				marginRight: theme.spacing(1),
				color: theme.palette.text.secondary,
				"& svg": { fontSize: 18 },
			},

			// Label styles
			"& .MuiTreeItem-label": {
				display: "flex",
				alignItems: "center",
				justifyContent: "space-between",
				fontSize: dense ? 13 : 14,
				lineHeight: 1.3,
				padding: theme.spacing(0.5, 1),
				width: "100%",
			},
		},
	};
});

interface CustomTreeViewProps {
	children: ReactNode | ReactNode[];
	dense?: boolean;
	defaultCollapseIcon: ReactNode;
	defaultExpandIcon: ReactNode;
	onNodeToggle: (event: SyntheticEvent, nodeIds: string[]) => void;
	expanded: string[];
	selected: string;
	multiSelect: boolean;
}

export const CustomTreeView: FC<CustomTreeViewProps> = ({
	children,
	dense,
	defaultCollapseIcon,
	defaultExpandIcon,
	onNodeToggle,
	expanded,
	selected,
	multiSelect,
}) => {
	// Bridge to MUI X v6 while preserving your existing props
	const selectedItems = selected ? [selected] : [];

	return (
		<StyledTreeView
			aria-label="tree"
			dense={dense}
			defaultCollapseIcon={defaultCollapseIcon}
			defaultExpandIcon={defaultExpandIcon}
			// v5-style props (ignored by v6 but kept for compatibility)
			// @ts-ignore
			expanded={expanded}
			// @ts-ignore
			selected={selected}
			// v6-style controlled props
			expandedItems={expanded}
			onExpandedItemsChange={(e, ids) =>
				onNodeToggle(e as SyntheticEvent, ids as string[])
			}
			selectedItems={selectedItems}
			onSelectedItemsChange={(_e, ids) => {
				// noop — selection is controlled upstream via `selected` (keep API stable)
				// If you want two-way control, expose an `onNodeSelect` prop and update there.
			}}
			multiSelect={multiSelect}
		>
			{children}
		</StyledTreeView>
	);
};
